import React, { useState, useEffect } from "react";
import { C, fmt, text } from "../theme";
import { api } from "../api";
import { Card, Notice, StatusPill as Badge, stageKindLabel } from "../components/ui";
import { usePermissions } from "../PermissionContext";

// Where each document type lives: the app page that opens it and the
// permission module that gates viewing it.
const DOC_TARGET = {
  AOR: { page: "aor", module: "aor" },
  PO: { page: "po", module: "po" },
  INVOICE: { page: "invoice", module: "invoice" },
  VIREMENT: { page: "virement", module: "virement" },
  PAYMENT_BATCH: { page: "payments", module: "payment" },
};

export default function ApprovalsPage({ onOpenDoc }) {
  const { can } = usePermissions();
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [coiChecked, setCoiChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.getPending(), api.getHistory()])
      .then(([p, h]) => { setPending(p); setHistory(h); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // The server acts as the signed-in session user; the response is surfaced
  // in a persistent banner so refusals are never silent.
  const post = async (path, body) => {
    try {
      const res = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setNotice({ kind: "error", text: data.error || `Request failed (${res.status})` });
      } else {
        setNotice({ kind: "success", text: data.message || "Done" });
      }
      load();
      // Tell the shell the workqueue changed so the sidebar badge refreshes.
      window.dispatchEvent(new Event("workqueue-changed"));
    } catch (err) {
      setNotice({ kind: "error", text: "Error: " + err.message });
    }
  };

  const handleApprove = (approvalId) =>
    post("/api/approvals/" + approvalId + "/approve", { coiDeclared: true });

  const handleReject = (approvalId) => {
    const reason = rejectReason.trim();
    if (!reason) return;
    setRejectingId(null);
    setRejectReason("");
    return post("/api/approvals/" + approvalId + "/reject", { comments: reason });
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  return (
    <div>
      <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: "0 0 16px" }}>Approvals</h1>

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: text.lg, fontWeight: 700, color: C.warning, marginBottom: 12 }}>Pending ({pending.length})</div>
        {pending.map((a) => {
          const batch = a.paymentBatch;
          const docLabel = a.aor?.aorNumber || a.po?.poNumber || a.invoice?.invoiceNumber || a.virement?.virNumber || batch?.batchNumber || "";
          const docDesc = a.aor?.description || a.po?.vendor || a.invoice?.vendor ||
            (a.virement ? `${a.virement.fromRef} → ${a.virement.toRef}` : "") ||
            (batch ? `Tier ${batch.tier} payment run • ${batch.itemCount} invoice${batch.itemCount === 1 ? "" : "s"}` : "");
          // POs and invoices are payable at gross; showing only the net
          // understates what the approver is binding (UX-33).
          const gross = a.po?.grossTotal || a.invoice?.grossTotal || 0;
          const net = a.po?.total ?? a.invoice?.total;
          const docTotal = gross || a.aor?.total || a.virement?.amount || batch?.totalAmount || net || 0;
          const amountNote = gross && net != null ? ` gross (net ${fmt(net)} + GST ${fmt((a.po?.gstTotal ?? a.invoice?.gstTotal) || 0)})` : "";
          const requestedBy = a.aor?.requestor?.displayName || a.po?.requestor?.displayName ||
            a.invoice?.submittedBy?.displayName || a.virement?.requestor?.displayName || batch?.preparedBy?.displayName;
          const lines = a.aor?.lineItems || a.po?.lineItems || a.invoice?.lineItems;
          const note = a.aor?.noteToApprover || a.virement?.reason;
          const target = DOC_TARGET[a.docType];
          const docId = a.aorId || a.poId || a.invoiceId || a.virementId || a.paymentBatchId;
          const canOpen = !!(onOpenDoc && target && docId && can(target.module, "view"));
          const checked = coiChecked[a.id] || false;
          const isSupport = a.stepType === "SUPPORT";
          const actionLabel = isSupport ? "Endorse" : "Approve";
          const injectedTL = isSupport && a.approverRole === "Team Lead" && a.layer === 1;
          const anyBu = a.docType === "INVOICE" && a.bu == null;
          // Mirror the server's RBAC gate so the page never offers an action
          // the API would refuse: payment batches need payment:approve,
          // virements accept virement:approve OR aor:approve (UX-45), all
          // other documents need aor:approve.
          const mayAct = a.docType === "PAYMENT_BATCH" ? can("payment", "approve")
            : a.docType === "VIREMENT" ? (can("virement", "approve") || can("aor", "approve"))
              : can("aor", "approve");
          const isRejecting = rejectingId === a.id;

          return (
            <div key={a.id} style={{ padding: 14, marginBottom: 8, background: C.warningBg, borderRadius: 8, border: `1px solid ${C.warningLight}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: text.lg, fontWeight: 700 }}>
                    {a.docType}:{" "}
                    {canOpen ? (
                      <button onClick={() => onOpenDoc(target.page, docId)} style={{
                        background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit",
                        fontSize: text.lg, fontWeight: 700, color: C.primary, textDecoration: "underline",
                      }}>{docLabel}</button>
                    ) : docLabel}
                    {batch?.isCherryPicked && (
                      <span style={{ marginLeft: 8, fontSize: text.xs, fontWeight: 700, color: C.purple, background: C.purpleBg, borderRadius: 10, padding: "2px 8px" }}>
                        ⚑ Exception — cherry-picked
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: text.sm, color: C.gray600 }}>{docDesc} • {fmt(docTotal)}{amountNote}{requestedBy ? <> • {batch ? "Prepared" : "Requested"} by {requestedBy}</> : null}</div>
                  <div style={{ fontSize: text.sm, color: C.gray600 }}>Layer {a.layer} • {a.approverRole}{a.bu && <span style={{ marginLeft: 6, fontSize: text.xs, color: C.gray500 }}>[{a.bu}]</span>} • {stageKindLabel(a.stepType)}</div>
                  {injectedTL && (
                    <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 2 }}>
                      This support stage was added automatically — submissions by anyone who is not a Team Lead route through a Team Lead endorsement first.
                    </div>
                  )}
                  {anyBu && (
                    <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 2 }}>
                      Invoice approval stages are not tied to one business unit — any holder of this layer may act.
                    </div>
                  )}
                </div>
              </div>

              {batch?.isCherryPicked && batch.exceptionReason && (
                <div style={{ padding: "8px 12px", marginBottom: 10, background: C.purpleBg, border: `1px solid ${C.purpleBorder}`, borderRadius: 8, fontSize: text.sm, color: C.gray700 }}>
                  <b style={{ color: C.purple }}>Override reason:</b> {batch.exceptionReason}
                </div>
              )}

              {lines?.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.sm, background: C.white, borderRadius: 8, marginBottom: 10 }}>
                  <thead><tr>{["Line item", "Qty", "Unit price", "Account", "Total"].map((h, i) => (
                    <th scope="col" key={i} style={{ padding: "6px 10px", textAlign: "left", borderBottom: `1px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>
                    {lines.map((li, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                        <td style={{ padding: "6px 10px" }}>{li.description}</td>
                        <td style={{ padding: "6px 10px" }}>{li.quantity}</td>
                        <td style={{ padding: "6px 10px" }}>{fmt(li.unitPrice)}</td>
                        <td style={{ padding: "6px 10px", color: C.gray600 }}>{li.accountCode || "—"}</td>
                        <td style={{ padding: "6px 10px", fontWeight: 600 }}>{fmt(li.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {note && (
                <div style={{ padding: "8px 12px", marginBottom: 10, background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: text.sm, color: C.gray700 }}>
                  <b>{a.virement ? "Justification" : "Note from requestor"}:</b> {note}
                </div>
              )}

              {!mayAct && (
                <div style={{ padding: "10px 14px", background: C.white, border: `1px solid ${C.gray300}`, borderRadius: 8, fontSize: text.sm, color: C.gray600 }}>
                  Your role can view this approval but cannot act on it — acting requires the <b>{a.docType === "PAYMENT_BATCH" ? "payment" : a.docType === "VIREMENT" ? "virement" : "aor"}:approve</b> permission. Ask an administrator to grant it.
                </div>
              )}

              {/* COI Declaration — a real checkbox (not a painted div), so the
                  binding compliance gate is keyboard- and SR-operable. */}
              {mayAct && <label style={{
                display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px",
                background: checked ? C.successBg : C.white, border: `1px solid ${checked ? C.successLight : C.gray300}`,
                borderRadius: 8, cursor: "pointer", marginBottom: 10,
              }}>
                <input type="checkbox" checked={checked}
                  onChange={() => setCoiChecked({ ...coiChecked, [a.id]: !checked })}
                  style={{ width: 20, height: 20, flexShrink: 0, margin: 0, accentColor: C.success, cursor: "pointer" }} />
                <div>
                  <div style={{ fontSize: text.sm, fontWeight: 700, color: checked ? C.success : C.gray800 }}>Declaration of No Conflict of Interest</div>
                  <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 2 }}>I declare no personal, financial, or professional interest that could influence my judgment.</div>
                </div>
              </label>}

              {mayAct && !isRejecting && (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button disabled={!checked} onClick={() => handleApprove(a.id)} style={{
                    background: checked ? C.success : C.gray200, color: checked ? C.white : C.gray400,
                    border: "none", borderRadius: 6, padding: "6px 14px", fontSize: text.sm, fontWeight: 600,
                    cursor: checked ? "pointer" : "not-allowed",
                  }}>{actionLabel}</button>
                  <button onClick={() => { setRejectingId(a.id); setRejectReason(""); }} style={{
                    background: C.danger, color: C.white, border: "none", borderRadius: 6,
                    padding: "6px 14px", fontSize: text.sm, fontWeight: 600, cursor: "pointer",
                  }}>Return to requestor</button>
                  {!checked && <span style={{ fontSize: text.xs, color: C.warning, fontWeight: 600, marginLeft: 4 }}>⚠ COI declaration required</span>}
                </div>
              )}

              {mayAct && isRejecting && (
                // Escape cancels the panel, matching the Cancel button (UX-83).
                <div onKeyDown={(e) => { if (e.key === "Escape") { setRejectingId(null); setRejectReason(""); } }}
                  style={{ padding: "10px 14px", background: C.white, border: `1px solid ${C.dangerLight}`, borderRadius: 8 }}>
                  <label htmlFor={`reject-reason-${a.id}`} style={{ display: "block", fontSize: text.sm, fontWeight: 700, color: C.gray800, marginBottom: 6 }}>
                    Reason for returning to requestor <span style={{ color: C.danger }}>*</span>
                  </label>
                  <textarea id={`reject-reason-${a.id}`} value={rejectReason} autoFocus rows={3}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Explain what must change before resubmission — the requestor sees this."
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: text.sm, fontFamily: "inherit", border: `1px solid ${C.gray300}`, borderRadius: 6, resize: "vertical" }} />
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <button disabled={!rejectReason.trim()} onClick={() => handleReject(a.id)} style={{
                      background: rejectReason.trim() ? C.danger : C.gray200, color: rejectReason.trim() ? C.white : C.gray400,
                      border: "none", borderRadius: 6, padding: "6px 14px", fontSize: text.sm, fontWeight: 600,
                      cursor: rejectReason.trim() ? "pointer" : "not-allowed",
                    }}>Confirm return</button>
                    <button onClick={() => { setRejectingId(null); setRejectReason(""); }} style={{
                      background: C.white, color: C.gray700, border: `1px solid ${C.gray300}`, borderRadius: 6,
                      padding: "6px 14px", fontSize: text.sm, fontWeight: 600, cursor: "pointer",
                    }}>Cancel</button>
                    {!rejectReason.trim() && <span style={{ fontSize: text.xs, color: C.danger, fontWeight: 600, marginLeft: 4, alignSelf: "center" }}>A reason is required</span>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {pending.length === 0 && <div style={{ color: C.gray500, padding: 12 }}>No pending approvals</div>}
      </Card>

      <Card>
        <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 12 }}>Approval History</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Document", "Layer", "Role", "Action", "By", "Date", "COI"].map((h, i) => (
            <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: C.primary }}>
                  <span style={{ fontWeight: 400, color: C.gray500, fontSize: text.xs, marginRight: 6 }}>{h.approval?.docType}</span>
                  {h.approval?.aor?.aorNumber || h.approval?.po?.poNumber || h.approval?.invoice?.invoiceNumber || h.approval?.virement?.virNumber || h.approval?.paymentBatch?.batchNumber || "—"}
                </td>
                <td style={{ padding: "10px 12px" }}>{h.approval?.layer}</td>
                <td style={{ padding: "10px 12px" }}>{h.approval?.approverRole}{h.approval?.bu && <span style={{ marginLeft: 6, fontSize: text.xs, color: C.gray500 }}>[{h.approval.bu}]</span>}</td>
                <td style={{ padding: "10px 12px" }}><Badge status={h.action} /></td>
                <td style={{ padding: "10px 12px" }}>{h.user?.displayName}</td>
                <td style={{ padding: "10px 12px" }}>{new Date(h.actionDate).toLocaleString()}</td>
                <td style={{ padding: "10px 12px", color: h.coiDeclared ? C.success : C.gray400 }}>{h.coiDeclared ? "✓ Yes" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}